/**
 * Vessel Store
 * Manages vessel state with preservation during polygon changes
 */
import { create } from 'zustand';
import { fetchVesselByMmsi, fetchVessels, fetchVesselsByArea, type VesselPosition } from '@/services/map/vesselService';

interface VesselStore {
  vessels: VesselPosition[];
  vesselsLoading: boolean;
  previousVessels: VesselPosition[];
  selectedPolygon: string | null;

  // Actions
  setVessels: (vessels: VesselPosition[]) => void;
  setVesselsLoading: (loading: boolean) => void;
  setSelectedPolygon: (polygon: string | null) => void;
  fetchVesselsByPolygon: (polygonWkt: string, getToken: () => Promise<string>) => Promise<void>;
  fetchAllVessels: (getToken: () => Promise<string>) => Promise<void>;
  getOrFetchVessel: (mmsi: string, getToken: () => Promise<string>) => Promise<VesselPosition | null>;
  preserveVessels: () => void;
  restoreVessels: () => void;
}

export const useVesselStore = create<VesselStore>((set, get) => ({
  vessels: [],
  vesselsLoading: false,
  previousVessels: [],
  selectedPolygon: null,

  setVessels: (vessels) => set({ vessels }),
  setVesselsLoading: (loading) => set({ vesselsLoading: loading }),
  setSelectedPolygon: (polygon) => set({ selectedPolygon: polygon }),

  preserveVessels: () => {
    const current = get().vessels;
    set({ previousVessels: current });
  },

  restoreVessels: () => {
    const previous = get().previousVessels;
    set({ vessels: previous, vesselsLoading: false });
  },

  getOrFetchVessel: async (mmsi, getToken) => {
    const state = get();
    // Check current vessels
    const found = state.vessels.find((v) => String(v.mmsi) === mmsi);
    if (found) return found;

    // Check previous vessels
    const foundPrev = state.previousVessels.find((v) => String(v.mmsi) === mmsi);
    if (foundPrev) return foundPrev;

    // Fetch from API
    return await fetchVesselByMmsi(getToken, mmsi);
  },

  fetchVesselsByPolygon: async (polygonWkt, getToken) => {
    if (!polygonWkt) {
      return;
    }

    const state = get();
    // Preserve current vessels so icons remain visible during polygon selection
    const currentVessels = state.vessels.length > 0 ? state.vessels : state.previousVessels;
    set({
      previousVessels: currentVessels,
      vesselsLoading: true,
      selectedPolygon: polygonWkt,
      // NOTE: We do NOT clear vessels here - they stay visible
    });

    try {
      const result = await fetchVesselsByArea(getToken, polygonWkt);

      // CRITICAL: Always update state with new vessels, even if result is empty or same as before
      // This forces the UI to re-render and Map component to receive the new list
      set({
        vessels: result || [], // Default to empty array if null/undefined
        vesselsLoading: false,
      });

      /*
            // Only update vessels if we got results
            // If result is empty, keep existing vessels visible
            if (result && result.length > 0) {
                set({
                    vessels: result,
                    vesselsLoading: false
                });
            } else {
                // No vessels in new area, but keep existing ones visible
                // Restore previous vessels if we had any
                set({
                    vessels: currentVessels, // Restore previous vessels
                    vesselsLoading: false
                });
            }
            */
    } catch (error) {
      console.error('Failed to fetch vessels by area:', error);
      // On error, restore previous vessels to keep them visible
      set({
        vessels: currentVessels, // Restore previous vessels
        vesselsLoading: false,
      });
    }
  },

  fetchAllVessels: async (getToken) => {
    set({ vesselsLoading: true });
    try {
      const result = await fetchVessels(getToken);
      set({ vessels: result, vesselsLoading: false });
    } catch (error) {
      console.error('Failed to fetch vessels:', error);
      set({ vessels: [], vesselsLoading: false });
    }
  },
}));
